'use client';

import { useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';

import LogoInPinkCircle from 'public/img/logo/vivrelle-logo-in-pink-circle.png';

import Text from '@/components/common/Text/Text';

import './MembershipApplicationOverlay.style.scss';

const OVERLAY_LABEL = 'Please stay on this page';
const OVERLAY_SUBTITLE = 'We are submitting your application';

type MembershipApplicationOverlayProps = {
  visible: boolean;
};

/**
 * Semi-opaque full-screen overlay shown during membership application submit.
 *
 * Reuses the existing vivrelle-logo-in-pink-circle.png asset
 * A hidden preload image is always mounted so the logo is cached before submit - even with slow network this prevents the logo from flashing
 */
export default function MembershipApplicationOverlay(
  props: MembershipApplicationOverlayProps
) {
  const { visible } = props;
  const [logoLoaded, setLogoLoaded] = useState(false);

  return (
    <>
      <Image
        src={LogoInPinkCircle}
        alt=''
        width={64}
        height={64}
        priority
        aria-hidden='true'
        className='MembershipApplicationOverlay__preload'
        onLoad={() => setLogoLoaded(true)}
      />
      {visible && (
        <div
          className='MembershipApplicationOverlay'
          role='status'
          aria-live='polite'
          aria-busy='true'
        >
          <div className='MembershipApplicationOverlay__content'>
            <div className='MembershipApplicationOverlay__logo-stage'>
              <span
                className='MembershipApplicationOverlay__ring'
                aria-hidden='true'
              />
              <span
                className='MembershipApplicationOverlay__ring MembershipApplicationOverlay__ring--delayed'
                aria-hidden='true'
              />
              <Image
                className={cn('MembershipApplicationOverlay__logo', {
                  'is-loaded': logoLoaded
                })}
                src={LogoInPinkCircle}
                alt='Vivrelle logo'
                width={64}
                height={64}
                priority
                onLoad={() => setLogoLoaded(true)}
              />
            </div>

            <Text
              className='MembershipApplicationOverlay__message'
              variant='title-xl'
              as='p'
            >
              {OVERLAY_LABEL}
              <span
                className='MembershipApplicationOverlay__dots'
                aria-hidden='true'
              >
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </Text>

            <Text
              className='MembershipApplicationOverlay__subtitle'
              variant='title-xxs-500-upper'
              as='p'
            >
              {OVERLAY_SUBTITLE}
            </Text>
          </div>
        </div>
      )}
    </>
  );
}
